// components/artifacts/renderers/MapArtifactRenderer.tsx
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { MapArtifact, MapData, MapMarker, MapRoute } from "@/types/Artifact";
import { MapPin, Navigation, ExternalLink, Loader2, Route, Lightbulb } from "lucide-react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { UseTheme } from "@/components/ThemeProvider";
import { trpc } from "@/lib/trpc";
import { useParams } from "react-router-dom";
import { createRoot } from "react-dom/client";

// Custom styles for dark mode popups
const popupStyles = `
  .maplibregl-popup-content {
    background: var(--background) !important;
    color: var(--foreground) !important;
    padding: 0 !important;
    border-radius: 8px !important;
  }
  .maplibregl-popup-tip {
    border-top-color: var(--background) !important;
  }
`;

// Map styles for light and dark mode
// Light: OpenFreeMap Liberty (more POIs)
// Dark: CARTO Dark Matter (reliable dark theme)
const MAP_STYLES = {
  light: "https://tiles.openfreemap.org/styles/liberty",
  dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
};

interface MapArtifactRendererProps {
  artifact: MapArtifact;
}

const parseMapData = (content: MapData | string): MapData | null => {
  if (typeof content === "string") {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }
  return content;
};

// Fetch route from OSRM (Open Source Routing Machine)
const fetchOSRMRoute = async (
  points: Array<{ lat: number; lng: number }>
): Promise<{ coordinates: Array<[number, number]>; distance?: number; duration?: number } | null> => {
  if (points.length < 2) return null;

  // Format coordinates for OSRM: lng,lat;lng,lat;...
  const coords = points.map((p) => `${p.lng},${p.lat}`).join(";");
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.code === "Ok" && data.routes?.[0]?.geometry?.coordinates) {
      return {
        coordinates: data.routes[0].geometry.coordinates as Array<[number, number]>,
        distance: data.routes[0].distance,
        duration: data.routes[0].duration,
      };
    }
  } catch (error) {
    console.error("OSRM routing failed:", error);
  }

  return null;
};

// Helper to determine if dark mode is active
const useIsDarkMode = () => {
  const { theme } = UseTheme();
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      if (theme === "dark") {
        setIsDark(true);
      } else if (theme === "system") {
        setIsDark(window.matchMedia("(prefers-color-scheme: dark)").matches);
      } else {
        setIsDark(false);
      }
    };

    checkDarkMode();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if (theme === "system") {
        setIsDark(mediaQuery.matches);
      }
    };

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [theme]);

  return isDark;
};

// Separate component for MapLibre
const MapLibreMap: React.FC<{
  center: { lat: number; lng: number };
  zoom: number;
  markers: MapMarker[];
  route?: MapRoute;
}> = ({ center, zoom, markers, route }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{ distance?: string; duration?: string } | null>(null);
  const [styleVersion, setStyleVersion] = useState(0); // Increments on style change
  const isInitializedRef = useRef(false);
  const isDarkMode = useIsDarkMode();
  const currentStyleRef = useRef<string>("");
  
  // Get threadId from URL params for conversation context
  const { threadId } = useParams<{ threadId?: string }>();
  
  // tRPC mutation for location enrichment
  const enrichLocationMutation = trpc.images.enrichLocation.useMutation();

  // Function to fit map to show all markers/route
  const fitMapToBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;

    const bounds = new maplibregl.LngLatBounds();
    let hasPoints = false;

    // Add route points to bounds
    if (route && route.points.length >= 2) {
      route.points.forEach((p) => {
        bounds.extend([p.lng, p.lat]);
        hasPoints = true;
      });
    }

    // Add marker positions to bounds
    markers.forEach((marker) => {
      bounds.extend([marker.lng, marker.lat]);
      hasPoints = true;
    });

    // Fit to bounds if we have points
    if (hasPoints) {
      // For single point, just center on it
      if (markers.length === 1 && (!route || route.points.length < 2)) {
        map.flyTo({
          center: [markers[0].lng, markers[0].lat],
          zoom: zoom,
          duration: 500,
        });
      } else {
        map.fitBounds(bounds, { 
          padding: 50,
          duration: 500,
          maxZoom: 16,
        });
      }
    }
  }, [isLoaded, markers, route, zoom]);

  // Detect when map becomes visible (tab switch) using IntersectionObserver
  useEffect(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && isLoaded) {
            // Small delay to ensure map is fully rendered
            setTimeout(() => {
              // Resize map to handle container size changes
              mapRef.current?.resize();
              // Fit to bounds
              fitMapToBounds();
            }, 100);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [isLoaded, fitMapToBounds]);

  // Initialize map only once
  useEffect(() => {
    if (!mapContainerRef.current || isInitializedRef.current) return;
    isInitializedRef.current = true;

    const initialStyle = isDarkMode ? MAP_STYLES.dark : MAP_STYLES.light;
    currentStyleRef.current = initialStyle;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: initialStyle,
      center: [center.lng, center.lat],
      zoom: zoom,
    });

    mapRef.current = map;

    // Handle WebGL context loss
    map.on("webglcontextlost", () => {
      console.warn("MapLibre WebGL context lost - attempting recovery");
    });

    map.on("webglcontextrestored", () => {
      console.log("MapLibre WebGL context restored");
      setIsLoaded(true);
    });

    map.on("load", () => {
      setIsLoaded(true);
    });

    map.on("error", (e) => {
      console.error("MapLibre error:", e);
    });

    return () => {
      if (mapRef.current) {
        // Clear all markers first
        markersRef.current.forEach((marker) => marker.remove());
        markersRef.current = [];
        
        // Remove map and release resources
        mapRef.current.remove();
        mapRef.current = null;
        isInitializedRef.current = false;
        setIsLoaded(false);
      }
    };
  }, []); // Empty dependency array - only run once

  // Update map style when theme changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;

    const newStyle = isDarkMode ? MAP_STYLES.dark : MAP_STYLES.light;
    
    // Only update if style actually changed
    if (currentStyleRef.current !== newStyle) {
      currentStyleRef.current = newStyle;
      
      // Store current view state
      const currentCenter = map.getCenter();
      const currentZoom = map.getZoom();
      
      // Set new style
      map.setStyle(newStyle);
      
      // Restore view and trigger route re-add after style loads
      map.once("style.load", () => {
        map.setCenter(currentCenter);
        map.setZoom(currentZoom);
        
        // Increment styleVersion to trigger route re-add
        setStyleVersion((v) => v + 1);
      });
    }
  }, [isDarkMode, isLoaded]);

  // Update markers and route when data changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

        // Add markers
        markers.forEach((marker, index) => {
      // Create container for pin + label
      const container = document.createElement("div");
      container.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
      `;
      
      // Create the pin element
      const pinEl = document.createElement("div");
      
      // Determine marker style based on route position
      let backgroundColor = "#3b82f6"; // Default blue
      
          if (route && route.points.length >= 2) {
            const isStart = index === 0 && marker.lat === route.points[0].lat && marker.lng === route.points[0].lng;
            const isEnd = index === markers.length - 1 && 
              marker.lat === route.points[route.points.length - 1].lat && 
              marker.lng === route.points[route.points.length - 1].lng;
            
            if (isStart) {
          backgroundColor = "#22c55e"; // Green for start
            } else if (isEnd) {
          backgroundColor = "#ef4444"; // Red for end
        }
      }

      pinEl.style.cssText = `
        width: 24px;
        height: 24px;
        background: ${backgroundColor};
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      `;
      
      container.appendChild(pinEl);

      // Add label if present
      if (marker.label) {
        const labelEl = document.createElement("div");
        labelEl.textContent = marker.label;
        labelEl.style.cssText = `
          margin-top: 4px;
          padding: 2px 6px;
          background: rgba(255, 255, 255, 0.95);
          color: #1f2937;
          font-size: 12px;
          font-weight: 600;
          border-radius: 4px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          white-space: nowrap;
          pointer-events: none;
        `;
        container.appendChild(labelEl);
      }

      const maplibreMarker = new maplibregl.Marker({ element: container })
        .setLngLat([marker.lng, marker.lat])
        .addTo(map);

      // Create interactive popup with AI enrichment
      const popup = new maplibregl.Popup({ 
        offset: 12,
        maxWidth: "400px",
        className: "ai-enriched-popup",
      });
      
      maplibreMarker.setPopup(popup);

      // On marker click, load AI-enriched content
      container.addEventListener("click", async () => {
        // Create a container div for React component
        const popupContainer = document.createElement("div");
        popupContainer.className = "map-popup-content";
        
        // Mount loading component
        const loadingRoot = createRoot(popupContainer);
        loadingRoot.render(
          <div className="text-sm p-3 min-w-[300px] bg-background text-foreground">
            <div className="flex items-center gap-2 mb-2">
              <strong className="text-gray-900 dark:text-gray-100">{marker.label || "Loading..."}</strong>
            </div>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>AI is gathering information...</span>
            </div>
          </div>
        );
        
        popup.setDOMContent(popupContainer);
        
        try {
          // Call AI to enrich location with conversation context
          const result = await enrichLocationMutation.mutateAsync({
            lat: marker.lat,
            lng: marker.lng,
            label: marker.label,
            name: marker.label,
            threadId: threadId, // Include threadId for personalized recommendations
          });

          if (result.success && result.location) {
            const { name, description, images, rating, hours, price, recommendation } = result.location;
            
            // Re-render with enriched content using React + Tailwind
            const enrichedRoot = createRoot(popupContainer);
            enrichedRoot.render(
              <div className="text-sm p-3 max-w-[380px] bg-background text-foreground">
                {/* Title */}
                <strong className="text-gray-900 dark:text-gray-100 text-base block mb-2">
                  {name}
                </strong>
                
                {/* Metadata (rating, price, hours) */}
                {(rating || price || hours) && (
                  <div className="mb-2 flex flex-wrap gap-2 items-center text-xs">
                    {rating && (
                      <span className="text-gray-900 dark:text-gray-100 font-semibold">{rating}</span>
                    )}
                    {price && (
                      <span className="text-gray-600 dark:text-gray-400">{price}</span>
                    )}
                    {hours && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <span className={hours.toLowerCase().includes("open") ? "text-green-600 dark:text-green-400 font-medium" : "text-red-600 dark:text-red-400 font-medium"}>
                          {hours}
                        </span>
                      </>
                    )}
                  </div>
                )}
                
                {/* Description */}
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-2">
                  {description}
                </p>
                
                {/* Personalized recommendation */}
                {recommendation && (
                  <div className="mt-3 p-2.5 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 border-l-3 border-blue-500 dark:border-blue-400 rounded-md">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-900 dark:text-gray-100 leading-relaxed text-xs">
                        {recommendation}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Image gallery */}
                {images && images.length > 0 && (
                  <div className={`mt-3 grid grid-cols-${Math.min(images.length, 3)} gap-1.5`}>
                    {images.slice(0, 3).map((img, idx) => (
                      <img
                        key={idx}
                        src={img.thumbnailUrl || img.url}
                        alt={img.alt}
                        className="w-full h-20 object-cover rounded-md cursor-pointer hover:opacity-90 transition"
                        onClick={() => window.open(img.url, '_blank')}
                        title="Click to view full size"
                      />
                    ))}
                  </div>
                )}
                
                {/* Coordinates */}
                <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="font-mono text-[11px] text-gray-400 dark:text-gray-500">
                    📍 {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
                  </span>
                </div>
              </div>
            );
          } else {
            throw new Error("Failed to fetch location data");
          }
        } catch (error) {
          console.error("Error enriching location:", error);
          
          // Fallback to basic popup with React
          const errorRoot = createRoot(popupContainer);
          errorRoot.render(
            <div className="text-sm p-3 bg-background text-foreground">
              <strong className="text-gray-900 dark:text-gray-100">{marker.label || "Location"}</strong>
              {marker.popup && (
                <p className="text-gray-700 dark:text-gray-300 mt-1">{marker.popup}</p>
              )}
              <span className="text-red-600 dark:text-red-400 text-xs mt-2 block">
                ⚠️ Unable to load AI information
              </span>
              <span className="font-mono text-[11px] text-gray-400 dark:text-gray-500 block mt-2">
                {marker.lat.toFixed(6)}, {marker.lng.toFixed(6)}
              </span>
            </div>
          );
        }
      });

      markersRef.current.push(maplibreMarker);
    });
  }, [markers, route, isLoaded, styleVersion]);

  // Handle route separately to avoid blocking
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isLoaded || !route || route.points.length < 2) return;

    const addRoute = async () => {
          const routeColor = route.color || "#3b82f6";
      const shouldUseRoads = route.useRoads !== false;

      // Remove existing route layer and source
      if (map.getLayer("route-layer")) {
        map.removeLayer("route-layer");
      }
      if (map.getSource("route-source")) {
        map.removeSource("route-source");
      }
          
          let routeCoords: Array<[number, number]>;
          
      if (shouldUseRoads) {
        const osrmResult = await fetchOSRMRoute(route.points);
            
        if (osrmResult) {
          routeCoords = osrmResult.coordinates;
          
          // Set route info
          if (osrmResult.distance && osrmResult.duration) {
            const distanceKm = (osrmResult.distance / 1000).toFixed(1);
            const durationMin = Math.round(osrmResult.duration / 60);
                setRouteInfo({
                  distance: `${distanceKm} km`,
                  duration: `${durationMin} min`,
                });
            }
          } else {
          // Fallback to direct line
          routeCoords = route.points.map((p) => [p.lng, p.lat] as [number, number]);
          }
      } else {
        routeCoords = route.points.map((p) => [p.lng, p.lat] as [number, number]);
      }

      // Check if map still exists (might have been unmounted)
      if (!mapRef.current) return;

      // Add route as GeoJSON source
      map.addSource("route-source", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: routeCoords,
          },
        },
      });

      // Add route layer
      map.addLayer({
        id: "route-layer",
        type: "line",
        source: "route-source",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": routeColor,
          "line-width": 5,
          "line-opacity": 0.8,
        },
      });

      // Fit map to route bounds
      const bounds = new maplibregl.LngLatBounds();
      routeCoords.forEach((coord) => {
        bounds.extend(coord as [number, number]);
      });
      map.fitBounds(bounds, { padding: 50 });
    };

    addRoute();
  }, [route, isLoaded, styleVersion]);

  return (
    <div className="relative h-full w-full" style={{ minHeight: "400px" }}>
      {!isLoaded && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-muted/20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
      {routeInfo && (
        <div className="absolute right-2 top-2 z-20 rounded-md bg-background/90 px-3 py-2 text-xs shadow-lg backdrop-blur">
          <div className="flex items-center gap-3">
            <Route className="h-4 w-4 text-blue-500" />
            <span className="font-medium">{routeInfo.distance}</span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground">{routeInfo.duration}</span>
          </div>
        </div>
      )}
      <div 
        ref={mapContainerRef} 
        className="absolute inset-0"
        style={{ minHeight: "400px" }}
      />
    </div>
  );
};

export const MapArtifactRenderer: React.FC<MapArtifactRendererProps> = React.memo(({ 
  artifact 
}) => {
  const mapData = useMemo(() => parseMapData(artifact.content), [artifact.content]);
  
  // Inject popup styles for dark mode support
  useEffect(() => {
    const styleId = "map-popup-dark-mode-styles";
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement("style");
      styleEl.id = styleId;
      styleEl.textContent = popupStyles;
      document.head.appendChild(styleEl);
    }
  }, []);

  if (!mapData || !mapData.center) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center rounded-lg bg-muted/30 p-4">
        <div className="text-center text-muted-foreground">
          <MapPin className="mx-auto mb-2 h-8 w-8 opacity-50" />
          <p>Invalid map data format</p>
          <p className="mt-1 text-xs">Expected: {"{ center: { lat, lng }, zoom?, markers?, route? }"}</p>
        </div>
      </div>
    );
  }

  const { center, zoom = 13, markers = [], route } = mapData;

  // Add center as a marker if no markers provided and no route
  const displayMarkers: MapMarker[] = markers.length > 0 
    ? markers 
    : route?.points?.length 
      ? route.points.map((p, i) => ({
          lat: p.lat,
          lng: p.lng,
          label: i === 0 ? "Start" : i === route.points.length - 1 ? "End" : `Point ${i + 1}`,
        }))
      : [{ lat: center.lat, lng: center.lng, label: "Location" }];

  const openInGoogleMaps = () => {
    if (route && route.points.length >= 2) {
      const origin = `${route.points[0].lat},${route.points[0].lng}`;
      const dest = `${route.points[route.points.length - 1].lat},${route.points[route.points.length - 1].lng}`;
      const waypoints = route.points.slice(1, -1).map(p => `${p.lat},${p.lng}`).join("|");
      const url = waypoints 
        ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&waypoints=${waypoints}`
        : `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}`;
      window.open(url, "_blank");
    } else {
      const url = `https://www.google.com/maps?q=${center.lat},${center.lng}`;
      window.open(url, "_blank");
    }
  };

  const openInOpenStreetMap = () => {
    const url = `https://www.openstreetmap.org/?mlat=${center.lat}&mlon=${center.lng}#map=${zoom}/${center.lat}/${center.lng}`;
    window.open(url, "_blank");
  };

  return (
    <div className="flex h-full flex-col" style={{ minHeight: "440px" }}>
      {/* Map Controls */}
      <div className="flex items-center justify-between border-b border-border/40 bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {route ? (
            <>
              <Route className="h-4 w-4 text-blue-500" />
              <span className="text-xs">{route.points.length} points</span>
            </>
          ) : (
            <>
              <Navigation className="h-4 w-4" />
              <span className="font-mono text-xs">
                {center.lat.toFixed(6)}, {center.lng.toFixed(6)}
              </span>
            </>
          )}
        </div>
        <div className="flex gap-1">
          <button
            onClick={openInOpenStreetMap}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Open in OpenStreetMap"
          >
            <ExternalLink className="h-3 w-3" />
            OSM
          </button>
          <button
            onClick={openInGoogleMaps}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title={route ? "Open directions in Google Maps" : "Open in Google Maps"}
          >
            <ExternalLink className="h-3 w-3" />
            {route ? "Directions" : "Google"}
          </button>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative flex-1" style={{ minHeight: "400px" }}>
        <MapLibreMap
            center={center}
            zoom={zoom}
            markers={displayMarkers}
            route={route}
          />
      </div>
    </div>
  );
});

MapArtifactRenderer.displayName = "MapArtifactRenderer";
